import { createContext, useContext, useState, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { useRevalidator } from '@remix-run/react';
import type { UserProfile } from '~/queries/profile';

interface ProfileContextValue {
  profile: UserProfile | null;
  updateProfile: (updates: Partial<UserProfile>) => void;
  revalidateProfile: () => void;
  profilePictureUrl: string | null;
}

const ProfileContext = createContext<ProfileContextValue | null>(null);

export const ProfileProvider = ({ 
  children, 
  serverProfile
}: { 
  children: ReactNode; 
  serverProfile: UserProfile | null;
}) => {
  const [profile, setProfile] = useState<UserProfile | null>(serverProfile);
  const revalidator = useRevalidator();
  const revalidatorRef = useRef(revalidator);
  revalidatorRef.current = revalidator;

  useEffect(() => {
    setProfile(serverProfile);
  }, [serverProfile]);

  const updateProfile = useCallback((updates: Partial<UserProfile>) => {
    setProfile(prev => prev ? { ...prev, ...updates } : null);
  }, []);

  const revalidateProfile = useCallback(() => {
    revalidatorRef.current.revalidate();
  }, []);

  const profilePictureUrl = profile?.profilePictureUrl && profile.updatedAt
    ? `/profile/picture?v=${profile.updatedAt}` 
    : null;

  const value = useMemo(() => ({
    profile,
    updateProfile,
    revalidateProfile,
    profilePictureUrl
  }), [profile, updateProfile, revalidateProfile, profilePictureUrl]);

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within ProfileProvider');
  }
  return context;
};
